/**
 * Inventory Analytics Page — INV-FG-8
 *
 * Six tabs:
 * 1. Dead Stock Detection (INV-FG-8-1)
 * 2. ITR (Inventory Turnover Ratio) (INV-FG-8-2)
 * 3. ABC Analysis (INV-FG-8-2)
 * 4. Seasonality (INV-FG-8-3)
 * 5. Transfer Suggestions (INV-FG-8-4)
 * 6. Supplier Catalog (INV-FG-8-5)
 *
 * API endpoints:
 * - GET /api/v1/inventory/dead-stock-analysis
 * - GET /api/v1/inventory/turnover-analysis
 * - GET /api/v1/inventory/abc-analysis
 * - GET /api/v1/inventory/seasonality-analysis
 * - GET /api/v1/inventory/seasonality-overview
 * - GET /api/v1/inventory/transfer-suggestions
 * - GET /api/v1/inventory/supplier-catalog/search
 */

import { api } from '@/api/client';
import type {
  AbcAnalysis,
  AbcClass,
  DeadStockAnalysis,
  DeadStockClassification,
  SeasonalTrend,
  SeasonalityAnalysis,
  SeasonalityOverviewItem,
  SupplierProduct,
  TransferSuggestionResult,
  TurnoverAnalysis,
} from '@/api/inventory';
import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BarChart3,
  Calendar,
  Loader2,
  Package,
  Search,
  ShoppingCart,
  Shuffle,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AnalyticsTab =
  | 'deadStock'
  | 'turnover'
  | 'abc'
  | 'seasonality'
  | 'transferSuggestions'
  | 'supplierCatalog';

// ============================================
// API Fetchers
// ============================================

async function fetchDeadStockAnalysis(): Promise<{ data: DeadStockAnalysis }> {
  return api.get<{ data: DeadStockAnalysis }>('/inventory/dead-stock-analysis');
}

async function fetchTurnoverAnalysis(year: number): Promise<{ data: TurnoverAnalysis }> {
  return api.get<{ data: TurnoverAnalysis }>(`/inventory/turnover-analysis?year=${year}`);
}

async function fetchAbcAnalysis(): Promise<{ data: AbcAnalysis }> {
  return api.get<{ data: AbcAnalysis }>('/inventory/abc-analysis');
}

async function fetchSeasonalityAnalysis(
  productId: string,
  year: number
): Promise<{ data: SeasonalityAnalysis }> {
  return api.get<{ data: SeasonalityAnalysis }>(
    `/inventory/seasonality-analysis?productId=${productId}&year=${year}`
  );
}

async function fetchSeasonalityOverview(): Promise<{ data: SeasonalityOverviewItem[] }> {
  return api.get<{ data: SeasonalityOverviewItem[] }>('/inventory/seasonality-overview');
}

async function fetchTransferSuggestions(): Promise<{ data: TransferSuggestionResult }> {
  return api.get<{ data: TransferSuggestionResult }>('/inventory/transfer-suggestions');
}

async function fetchSupplierCatalog(
  supplier: string,
  query: string
): Promise<{ data: SupplierProduct[] }> {
  return api.get<{ data: SupplierProduct[] }>(
    `/inventory/supplier-catalog/search?supplier=${encodeURIComponent(supplier)}&query=${encodeURIComponent(query)}`
  );
}

// ============================================
// Classification Badge
// ============================================

function ClassificationBadge({ classification }: { classification: DeadStockClassification }) {
  const { t } = useTranslation();

  const config: Record<DeadStockClassification, { label: string; className: string }> = {
    ACTIVE: {
      label: t('inventory.deadStock.activeLabel'),
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    },
    WARNING: {
      label: t('inventory.deadStock.warningLabel'),
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    DEAD: {
      label: t('inventory.deadStock.deadLabel'),
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    },
  };

  const { label, className } = config[classification];

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

function AbcBadge({ abcClass }: { abcClass: AbcClass }) {
  const config: Record<AbcClass, string> = {
    A: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    B: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    C: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${config[abcClass]}`}
    >
      {abcClass}
    </span>
  );
}

// ============================================
// Loading & Error
// ============================================

function LoadingSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
    </div>
  );
}

function ErrorState({ error }: { error: Error | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <AlertTriangle className="h-6 w-6 text-destructive" />
      <p className="text-destructive">{t('report.errorLoading')}</p>
      <p className="text-sm text-muted-foreground">{error?.message ?? ''}</p>
    </div>
  );
}

function formatHuf(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

// ============================================
// Dead Stock Tab
// ============================================

function DeadStockTab() {
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['inventory-dead-stock'],
    queryFn: fetchDeadStockAnalysis,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorState error={error} />;

  const analysis = data?.data;
  if (!analysis || analysis.items.length === 0) {
    return (
      <p className="py-8 text-center text-muted-foreground">{t('inventory.deadStock.noData')}</p>
    );
  }

  const { items, summary } = analysis;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">
            {t('inventory.deadStock.activeCount')}
          </div>
          <div className="mt-1 text-2xl font-bold text-green-600">{summary.activeCount}</div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">
            {t('inventory.deadStock.warningCount')}
          </div>
          <div className="mt-1 text-2xl font-bold text-yellow-600">{summary.warningCount}</div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">{t('inventory.deadStock.deadCount')}</div>
          <div className="mt-1 text-2xl font-bold text-red-600">{summary.deadCount}</div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">
            {t('inventory.deadStock.totalDeadValue')}
          </div>
          <div className="mt-1 text-2xl font-bold text-foreground">
            {formatHuf(summary.totalDeadValue)}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
        <h3 className="mb-4 text-lg font-semibold text-foreground">
          {t('inventory.deadStock.title')}
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.productName')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.sku')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.quantity')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.lastMovement')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.daysSinceMovement')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.classification')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.value')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.deadStock.warehouse')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                    {item.productName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {item.sku}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                    {item.quantity}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {item.lastMovementDate
                      ? new Date(item.lastMovementDate).toLocaleDateString('hu-HU')
                      : t('inventory.deadStock.noMovement')}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                    {item.daysSinceMovement}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-center">
                    <ClassificationBadge classification={item.classification} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                    {formatHuf(item.estimatedValue)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {item.warehouseName}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Turnover (ITR) Tab
// ============================================

function TurnoverTab() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['inventory-turnover', year],
    queryFn: () => fetchTurnoverAnalysis(year),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorState error={error} />;

  const analysis = data?.data;
  if (!analysis) {
    return (
      <p className="py-8 text-center text-muted-foreground">{t('inventory.turnover.noData')}</p>
    );
  }

  const TrendIcon =
    analysis.trend === 'up' ? ArrowUp : analysis.trend === 'down' ? ArrowDown : ArrowRight;
  const trendColor =
    analysis.trend === 'up'
      ? 'text-green-600'
      : analysis.trend === 'down'
        ? 'text-red-600'
        : 'text-muted-foreground';
  const trendLabel =
    analysis.trend === 'up'
      ? t('inventory.turnover.trendUp')
      : analysis.trend === 'down'
        ? t('inventory.turnover.trendDown')
        : t('inventory.turnover.trendStable');

  return (
    <div className="space-y-6">
      {/* Year selector */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">
          {t('inventory.turnover.year')}:
        </span>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          {[currentYear - 2, currentYear - 1, currentYear].map(y => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                year === y
                  ? 'bg-white text-foreground shadow-sm dark:bg-gray-700'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* KPI card */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">{t('inventory.turnover.ratio')}</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-foreground">
              {analysis.turnoverRatio.toFixed(2)}
            </span>
            <TrendIcon className={`h-5 w-5 ${trendColor}`} />
          </div>
          <div className={`mt-1 text-xs ${trendColor}`}>{trendLabel}</div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">{t('inventory.turnover.cogs')}</div>
          <div className="mt-1 text-2xl font-bold text-foreground">{formatHuf(analysis.cogs)}</div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">
            {t('inventory.turnover.avgInventoryValue')}
          </div>
          <div className="mt-1 text-2xl font-bold text-foreground">
            {formatHuf(analysis.averageInventoryValue)}
          </div>
        </div>
        {analysis.previousYearRatio !== null && (
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
            <div className="text-sm text-muted-foreground">
              {t('inventory.turnover.previousYear')}
            </div>
            <div className="mt-1 text-2xl font-bold text-muted-foreground">
              {analysis.previousYearRatio.toFixed(2)}
            </div>
          </div>
        )}
      </div>

      {/* Explanation card */}
      <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
        <h3 className="mb-2 text-lg font-semibold text-foreground">
          {t('inventory.turnover.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          ITR = COGS / Average Inventory Value. A higher ratio indicates efficient inventory
          management.
        </p>
      </div>
    </div>
  );
}

// ============================================
// ABC Analysis Tab
// ============================================

function AbcTab() {
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['inventory-abc'],
    queryFn: fetchAbcAnalysis,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorState error={error} />;

  const analysis = data?.data;
  if (!analysis || analysis.items.length === 0) {
    return <p className="py-8 text-center text-muted-foreground">{t('inventory.abc.noData')}</p>;
  }

  const { items, summary } = analysis;
  const totalProducts = summary.aCount + summary.bCount + summary.cCount;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <AbcBadge abcClass="A" />
            <span className="text-sm text-muted-foreground">{t('inventory.abc.aItems')}</span>
          </div>
          <div className="mt-1 text-2xl font-bold text-green-600">{summary.aCount}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {totalProducts > 0 ? `${Math.round((summary.aCount / totalProducts) * 100)}%` : '0%'}
          </div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <AbcBadge abcClass="B" />
            <span className="text-sm text-muted-foreground">{t('inventory.abc.bItems')}</span>
          </div>
          <div className="mt-1 text-2xl font-bold text-blue-600">{summary.bCount}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {totalProducts > 0 ? `${Math.round((summary.bCount / totalProducts) * 100)}%` : '0%'}
          </div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <AbcBadge abcClass="C" />
            <span className="text-sm text-muted-foreground">{t('inventory.abc.cItems')}</span>
          </div>
          <div className="mt-1 text-2xl font-bold text-muted-foreground">{summary.cCount}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {totalProducts > 0 ? `${Math.round((summary.cCount / totalProducts) * 100)}%` : '0%'}
          </div>
        </div>
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
          <div className="text-sm text-muted-foreground">{t('inventory.abc.totalRevenue')}</div>
          <div className="mt-1 text-2xl font-bold text-foreground">
            {formatHuf(summary.totalRevenue)}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
        <h3 className="mb-4 text-lg font-semibold text-foreground">{t('inventory.abc.title')}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.abc.class')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.abc.product')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.abc.sku')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.abc.revenue')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.abc.cumulativePercent')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {items.map(item => (
                <tr key={item.productId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="whitespace-nowrap px-4 py-3 text-center">
                    <AbcBadge abcClass={item.abcClass} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                    {item.productName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {item.sku}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                    {formatHuf(item.revenue)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.min(item.cumulativePercent, 100)}%` }}
                        />
                      </div>
                      <span>{item.cumulativePercent.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Seasonality Tab (INV-FG-8-3)
// ============================================

function TrendBadge({ trend }: { trend: SeasonalTrend }) {
  const { t } = useTranslation();

  const config: Record<SeasonalTrend, { label: string; className: string }> = {
    PEAK: {
      label: t('inventory.seasonality.peak'),
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    },
    NORMAL: {
      label: t('inventory.seasonality.normal'),
      className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    },
    LOW: {
      label: t('inventory.seasonality.low'),
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    },
  };

  const { label, className } = config[trend];

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

const MONTH_NAMES_HU = [
  'Jan',
  'Feb',
  'Már',
  'Ápr',
  'Máj',
  'Jún',
  'Júl',
  'Aug',
  'Sze',
  'Okt',
  'Nov',
  'Dec',
];

function SeasonalityTab() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [selectedProductId, setSelectedProductId] = useState('');

  // Overview: top 10 most seasonal products
  const overviewQuery = useQuery({
    queryKey: ['inventory-seasonality-overview'],
    queryFn: fetchSeasonalityOverview,
    staleTime: 5 * 60 * 1000,
  });

  // Detailed analysis for selected product
  const detailQuery = useQuery({
    queryKey: ['inventory-seasonality', selectedProductId, year],
    queryFn: () => fetchSeasonalityAnalysis(selectedProductId, year),
    enabled: selectedProductId.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const overview = overviewQuery.data?.data;
  const detail = detailQuery.data?.data;

  return (
    <div className="space-y-6">
      {/* Product selector + year picker */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('inventory.seasonality.selectProduct')}
          </label>
          <select
            className="w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-sm text-foreground dark:border-gray-600"
            value={selectedProductId}
            onChange={e => setSelectedProductId(e.target.value)}
          >
            <option value="">{t('inventory.seasonality.chooseProduct')}</option>
            {overview?.map(item => (
              <option key={item.productId} value={item.productId}>
                {item.productName} ({item.sku})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('inventory.turnover.year')}
          </label>
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
            {[currentYear - 1, currentYear].map(y => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  year === y
                    ? 'bg-white text-foreground shadow-sm dark:bg-gray-700'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview: most seasonal products */}
      {overviewQuery.isLoading && <LoadingSkeleton />}
      {overviewQuery.isError && <ErrorState error={overviewQuery.error} />}
      {overview && overview.length > 0 && !selectedProductId && (
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            {t('inventory.seasonality.topSeasonal')}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.seasonality.productName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.deadStock.sku')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.seasonality.index')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.seasonality.peakMonths')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.seasonality.lowMonths')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {overview.map(item => (
                  <tr
                    key={item.productId}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => setSelectedProductId(item.productId)}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                      {item.productName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                      {item.sku}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold text-foreground">
                      {item.seasonalIndex.toFixed(2)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-green-600">
                      {item.peakMonths.map(m => MONTH_NAMES_HU[m - 1]).join(', ') || '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-blue-600">
                      {item.lowMonths.map(m => MONTH_NAMES_HU[m - 1]).join(', ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed product analysis with bar chart */}
      {selectedProductId && detailQuery.isLoading && <LoadingSkeleton />}
      {selectedProductId && detailQuery.isError && <ErrorState error={detailQuery.error} />}
      {detail && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
              <div className="text-sm text-muted-foreground">
                {t('inventory.seasonality.index')}
              </div>
              <div className="mt-1 text-3xl font-bold text-foreground">
                {detail.seasonalIndex.toFixed(2)}
              </div>
            </div>
            <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
              <div className="text-sm text-muted-foreground">
                {t('inventory.seasonality.peakMonths')}
              </div>
              <div className="mt-1 text-lg font-bold text-green-600">
                {detail.peakMonths.map(m => MONTH_NAMES_HU[m - 1]).join(', ') || '-'}
              </div>
            </div>
            <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
              <div className="text-sm text-muted-foreground">
                {t('inventory.seasonality.lowMonths')}
              </div>
              <div className="mt-1 text-lg font-bold text-blue-600">
                {detail.lowMonths.map(m => MONTH_NAMES_HU[m - 1]).join(', ') || '-'}
              </div>
            </div>
          </div>

          {/* Monthly bar chart (CSS bars) */}
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
            <h3 className="mb-4 text-lg font-semibold text-foreground">
              {detail.productName} — {detail.year}
            </h3>
            <div className="flex items-end gap-2" style={{ height: 200 }}>
              {detail.monthlyData.map(md => {
                const maxQty = Math.max(...detail.monthlyData.map(d => d.avgQuantity), 1);
                const heightPct = maxQty > 0 ? (md.avgQuantity / maxQty) * 100 : 0;
                const barColor =
                  md.trend === 'PEAK'
                    ? 'bg-green-500'
                    : md.trend === 'LOW'
                      ? 'bg-blue-400'
                      : 'bg-gray-400 dark:bg-gray-500';
                return (
                  <div key={md.month} className="flex flex-1 flex-col items-center gap-1">
                    <span className="text-xs text-muted-foreground">
                      {md.avgQuantity.toFixed(0)}
                    </span>
                    <div
                      className={`w-full rounded-t ${barColor}`}
                      style={{ height: `${Math.max(heightPct, 2)}%` }}
                    />
                    <span className="text-xs font-medium text-muted-foreground">
                      {MONTH_NAMES_HU[md.month - 1]}
                    </span>
                    <TrendBadge trend={md.trend} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!selectedProductId && overview && overview.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">
          {t('inventory.seasonality.noData')}
        </p>
      )}
    </div>
  );
}

// ============================================
// Transfer Suggestions Tab (INV-FG-8-4)
// ============================================

function TransferSuggestionsTab() {
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['inventory-transfer-suggestions'],
    queryFn: fetchTransferSuggestions,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorState error={error} />;

  const result = data?.data;
  if (!result || result.suggestions.length === 0) {
    return (
      <p className="py-8 text-center text-muted-foreground">
        {t('inventory.transferSuggestion.noSuggestions')}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary card */}
      <div className="kgc-card-bg rounded-lg border border-gray-200 p-5 dark:border-gray-700">
        <div className="text-sm text-muted-foreground">
          {t('inventory.transferSuggestion.totalSuggestions')}
        </div>
        <div className="mt-1 text-2xl font-bold text-foreground">{result.suggestions.length}</div>
      </div>

      {/* Suggestions table */}
      <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
        <h3 className="mb-4 text-lg font-semibold text-foreground">
          {t('inventory.transferSuggestion.title')}
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.transferSuggestion.product')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.transferSuggestion.from')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.transferSuggestion.to')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.transferSuggestion.currentFrom')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.transferSuggestion.currentTo')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.transferSuggestion.suggestedQty')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('inventory.transferSuggestion.reason')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {result.suggestions.map((s, idx) => (
                <tr
                  key={`${s.productId}-${s.fromWarehouseId}-${idx}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                    {s.productName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {s.fromWarehouseName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {s.toWarehouseName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                    {s.currentQtyFrom}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-red-500">
                    {s.currentQtyTo}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold text-primary">
                    {s.suggestedQty}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {s.reason}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Supplier Catalog Tab (INV-FG-8-5)
// ============================================

const SUPPLIERS = [
  { code: 'stihl', name: 'STIHL' },
  { code: 'kemper', name: 'Kemper' },
  { code: 'powerbelt', name: 'Power Belt' },
];

function SupplierCatalogTab() {
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState('stihl');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['supplier-catalog', supplier, searchQuery],
    queryFn: () => fetchSupplierCatalog(supplier, searchQuery),
    enabled: searchQuery.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const handleSearch = () => {
    if (searchInput.trim().length > 0) {
      setSearchQuery(searchInput.trim());
    }
  };

  const products = data?.data;

  return (
    <div className="space-y-6">
      {/* Search controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('inventory.supplierCatalog.supplier')}
          </label>
          <select
            className="rounded-md border border-gray-300 bg-background px-3 py-2 text-sm text-foreground dark:border-gray-600"
            value={supplier}
            onChange={e => {
              setSupplier(e.target.value);
              setSearchQuery('');
            }}
          >
            {SUPPLIERS.map(s => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('inventory.supplierCatalog.search')}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-sm text-foreground dark:border-gray-600"
              placeholder={t('inventory.supplierCatalog.searchPlaceholder')}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
            <Button variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {isLoading && <LoadingSkeleton />}
      {isError && <ErrorState error={error} />}
      {searchQuery && products && products.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">
          {t('inventory.supplierCatalog.noResults')}
        </p>
      )}
      {products && products.length > 0 && (
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            {t('inventory.supplierCatalog.results')} ({products.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.supplierCatalog.productName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.supplierCatalog.category')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.supplierCatalog.price')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.supplierCatalog.availability')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('inventory.supplierCatalog.leadTime')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {products.map(product => (
                  <tr key={product.sku} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-foreground">{product.name}</div>
                      <div className="text-xs text-muted-foreground">{product.description}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                      {product.sku}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                      {product.category}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-foreground">
                      {formatHuf(product.priceNet)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          product.available
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}
                      >
                        {product.available
                          ? t('inventory.supplierCatalog.inStock')
                          : t('inventory.supplierCatalog.outOfStock')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                      {product.leadTimeDays} {t('inventory.supplierCatalog.days')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!searchQuery && (
        <div className="kgc-card-bg rounded-lg border border-gray-200 p-8 text-center dark:border-gray-700">
          <ShoppingCart className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-muted-foreground">{t('inventory.supplierCatalog.hint')}</p>
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Page
// ============================================

export function InventoryAnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('deadStock');

  const tabs: { id: AnalyticsTab; label: string; icon: typeof Package }[] = [
    { id: 'deadStock', label: t('inventory.deadStock.title'), icon: Package },
    { id: 'turnover', label: t('inventory.turnover.title'), icon: TrendingUp },
    { id: 'abc', label: t('inventory.abc.title'), icon: BarChart3 },
    { id: 'seasonality', label: t('inventory.seasonality.title'), icon: Calendar },
    { id: 'transferSuggestions', label: t('inventory.transferSuggestion.title'), icon: Shuffle },
    { id: 'supplierCatalog', label: t('inventory.supplierCatalog.title'), icon: ShoppingCart },
  ];

  return (
    <div className="kgc-bg min-h-screen p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('inventory.analytics.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('inventory.analytics.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/inventory')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'deadStock' && <DeadStockTab />}
      {activeTab === 'turnover' && <TurnoverTab />}
      {activeTab === 'abc' && <AbcTab />}
      {activeTab === 'seasonality' && <SeasonalityTab />}
      {activeTab === 'transferSuggestions' && <TransferSuggestionsTab />}
      {activeTab === 'supplierCatalog' && <SupplierCatalogTab />}
    </div>
  );
}
